import SimplePagination from '@Components/Common/SimplePagination';
import SimpleSort from '@Components/Common/SimpleSort';
import DialogTextField from '@Components/General/dialog-content/text-field';
import QuickOptions, { QuickOption } from '@Components/General/QuickOptions';
import Toolbar from '@Components/General/Toolbar';
import useDialog from '@hooks/useDialog';
import useEmptySearchParams from '@hooks/useEmptySearchParams';
import useHeader from '@hooks/useHeader';
import useSimplePagination from '@hooks/useSimplePagination';
import useSimpleSort from '@hooks/useSimpleSort';
import {
    DashboardCustomizeOutlined,
    DeleteOutlineRounded,
    DriveFileMoveOutlined,
    DriveFileRenameOutlineRounded,
    MailOutlineRounded,
} from '@mui/icons-material';
import { Box } from '@mui/material';
import {
    useMutation,
    useQueryClient,
    useSuspenseQuery,
} from '@tanstack/react-query';
import { useLoaderDeps, useNavigate } from '@tanstack/react-router';
import { emailTemplatesQueryOptions } from '@utils/queryOptions';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import NoTemplates from './no-templates';
import NoTemplatesFound from './no-templates-found';
import { closeOptions } from '@utils/utils';
import TemplateVariants from './variants';
import TemplateCard from './template-card';
import TemplateSelect from './template-select';

const TemplateQuickActions = ({ template }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dialog = useDialog();
    const queryClient = useQueryClient();
    const queryKey = emailTemplatesQueryOptions(useLoaderDeps({})).queryKey;
    const { control } = useForm({});
    const [templateSelectOpen, setTemplateSelectOpen] = useState(false);

    const updateTemplate = useMutation({
        mutationKey: ['updateEmailTemplate'],
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey });
            setTemplateSelectOpen(false);
            dialog.close();
            closeOptions();
        },
    });

    const { mutate: handleDuplicate } = useMutation({
        mutationKey: ['duplicateEmailTemplate'],
        onSuccess: res => {
            navigate({ to: res.id });
        },
    });

    const { mutate: createEmailDraft } = useMutation({
        mutationKey: ['createEmailDraft'],
        onSuccess: res => {
            navigate({ to: `../drafts/${res.id}/edit` });
        },
    });

    const deleteMutation = useMutation({
        mutationKey: ['deleteEmailTemplate'],
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey });
            dialog.close();
        },
    });

    const quickOptions: QuickOption[] = [
        {
            label: t('Rename'),
            onClick: () => {
                dialog?.open({
                    title: t('Rename Template'),
                    confirmLabel: t('Rename'),
                    content: (
                        <DialogTextField
                            name="parent_title"
                            control={control}
                            defaultValue={template.parent_title}
                            label={t('New Title')}
                        />
                    ),
                    onConfirm: () => [template.id, control._formValues],
                    confirmMutation: updateTemplate,
                });
            },
            Icon: DriveFileRenameOutlineRounded,
        },
        {
            label: t('Add Variant'),
            onClick: () => handleDuplicate(template.id),
            Icon: DashboardCustomizeOutlined,
        },
        ...(template.variants_count === 1
            ? [
                  {
                      label: t('Move to'),
                      onClick: () => setTemplateSelectOpen(true),
                      Icon: DriveFileMoveOutlined,
                  },
                  {
                      label: t('Compose Email'),
                      onClick: () =>
                          createEmailDraft({
                              subject: t('Untitled Email'),
                              template: template.template,
                              // parent_template: template.id,
                          }),
                      Icon: MailOutlineRounded,
                  },
              ]
            : []),
        {
            label: t('Delete'),
            Icon: DeleteOutlineRounded,
            sx: { color: 'error.main' },
            onClick: () => {
                dialog.open({
                    title: t('Delete Template') + '?',
                    message: (
                        <Trans
                            i18nKey="prompts:content.deleteTemplate"
                            values={{
                                parent_title: template.parent_title,
                                count: template.variants_count,
                            }}
                            count={template.variants_count}
                        />
                    ),
                    onConfirm: () => template.id,
                    confirmMutation: deleteMutation,
                    confirmButtonProps: { color: 'error' },
                    confirmLabel: t('Delete'),
                });
            },
        },
    ];

    const handleMoveSelect = temp => {
        updateTemplate.mutate([template.id, { parent: temp.id }]);
    };

    return (
        <>
            <TemplateSelect
                open={templateSelectOpen}
                onClose={() => setTemplateSelectOpen(false)}
                template={template}
                onSelect={handleMoveSelect}
                actionText={t('Move')}
            />
            <QuickOptions size="small" options={quickOptions} />
        </>
    );
};

const EmailTemplates = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [selectedTemplate, setSelectedTemplate] = useState(null);

    const searchParamsEmpty = useEmptySearchParams();

    const simplePageHook = useSimplePagination();

    const simpleSortHook = useSimpleSort({
        inverseFields: ['created', 'updated'],
        defaultSortField: 'title',
    });

    const headerOptions = useMemo(() => {
        return {
            title: t('Templates'),
            searchProps: {
                searchPlacholder: t('search:email-templates'),
            },
            fabAction: () => () => navigate({ to: 'new' }),
        };
    }, []);

    useHeader(headerOptions);

    const {
        data: { results, count },
    } = useSuspenseQuery(emailTemplatesQueryOptions(useLoaderDeps({})));

    if (results.length === 0 && searchParamsEmpty) {
        return <NoTemplates />;
    }

    const handleCardClick = template => {
        if (template.variants_count === 1) {
            navigate({ to: template.id });
            return;
        }
        setSelectedTemplate(template);
    };

    return (
        <>
            <Toolbar>
                <SimpleSort
                    sortOptions={[
                        { value: 'title', label: t('Title') },
                        { value: 'created', label: t('Last Created') },
                        { value: 'updated', label: t('Last Updated') },
                    ]}
                    {...simpleSortHook}
                />
                <SimplePagination count={count} {...simplePageHook} />
            </Toolbar>
            <TemplateVariants
                selectedTemplate={selectedTemplate}
                setSelectedTemplate={setSelectedTemplate}
                open={Boolean(selectedTemplate)}
                onClose={() => setSelectedTemplate(null)}
                onSelect={temp => navigate({ to: temp.id })}
            />
            {results.length === 0 ? (
                <NoTemplatesFound />
            ) : (
                <Box
                    display="grid"
                    gap={2}
                    gridTemplateColumns="repeat(auto-fill, minmax(218px, 1fr))"
                >
                    {results.map(template => (
                        <TemplateCard
                            key={template.id}
                            size="large"
                            template={template}
                            onClick={handleCardClick}
                            cardActions={
                                <TemplateQuickActions template={template} />
                            }
                        />
                    ))}
                </Box>
            )}
        </>
    );
};

export default EmailTemplates;
